import React, { useState } from "react";
import { X } from "lucide-react";
import { apiPostFormData } from "../../API/Api_Url";
import { toast } from "react-toastify";

function FreelancerAddJobPostTrackerModal({ isOpen, onClose, selectedReq, GetAllAssignedJobPosts }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [status, setStatus] = useState("");
    const [date, setDate] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    };

    const removeFile = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
    };

    const handleSubmit = async () => {
        if (!selectedReq) {
            toast.error("No job post selected.");
            return;
        }
        if (!title.trim()) {
            toast.error("Title is required.");
            return;
        }
        if (!description.trim()) {
            toast.error("Description is required.");
            return;
        }
        if (!status) {
            toast.error("Status is required.");
            return;
        }
        if (!date) {
            toast.error("Date is required.");
            return;
        }
        if (selectedFile) {
            const validImageTypes = ["image/jpeg", "image/png", "image/jpg"];
            const validVideoTypes = ["video/mp4", "video/quicktime"];
            if (
                !validImageTypes.includes(selectedFile.type) &&
                !validVideoTypes.includes(selectedFile.type)
            ) {
                toast.error("Please select a valid image or video file.");
                return;
            }
        }

        try {
            setIsLoading(true);
            const formData = new FormData();
            formData.append("jobPostId", selectedReq?._id);
            formData.append("Title", title);
            formData.append("Description", description);
            formData.append("Status", status);
            formData.append("Date", date);

            if (selectedFile) {
                if (selectedFile.type.startsWith("image/")) {
                    formData.append("Image", selectedFile);
                } else if (selectedFile.type.startsWith("video/")) {
                    formData.append("Video", selectedFile);
                }
            }

            const res = await apiPostFormData(
                "api/freelance-engineer/AddJobPostTracker",
                formData
            );

            if (res?.data) {
                toast.success(res?.message || "Tracker added.")
                GetAllAssignedJobPosts?.(res.data);
                onClose();
            }
        } catch (error) {
            console.error("Error adding job post tracker:", error);
            alert("Failed to add tracker. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };


    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 relative 
                  max-h-[90vh] overflow-y-auto">
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
                >
                    <X className="w-5 h-5" />
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                    Add Job Post Tracker
                </h2>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Title</label>
                        <input
                            type="text"
                            className="w-full border rounded-lg p-2 mt-1 text-sm"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <input
                            className="w-full border rounded-lg p-2 mt-1 text-sm"
                            rows="3"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <select
                            className="w-full border rounded-lg p-2 mt-1 text-sm"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            required
                        >
                            <option value="">Select Status</option>
                            <option value="Pending">Pending</option>
                            <option value="Ongoing">Ongoing</option>
                            <option value="Completed">Completed</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Date</label>
                        <input
                            type="date"
                            className="w-full border rounded-lg p-2 mt-1 text-sm"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            required
                        />
                    </div>

                    <div className="space-y-3">
                        <label className="block text-sm font-medium text-gray-700">
                            Upload Media (Image/Video)
                        </label>

                        <input
                            type="file"
                            accept="image/*,video/*"
                            className="w-full border rounded-lg p-2 mt-1 text-sm"
                            onChange={handleFileChange}
                        />

                        {previewUrl && (
                            <div className="mt-3">
                                {selectedFile.type.startsWith("image/") ? (
                                    <img
                                        src={previewUrl}
                                        alt="Preview"
                                        className="w-40 h-40 object-cover rounded-lg border"
                                    />
                                ) : (
                                    <video
                                        src={previewUrl}
                                        controls
                                        className="w-60 rounded-lg border"
                                    />
                                )}

                                <button
                                    type="button"
                                    onClick={removeFile}
                                    className="mt-2 px-3 py-1 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600"
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg border text-sm text-gray-700 hover:bg-gray-100"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => { handleSubmit() }}
                            disabled={isLoading}
                            className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-50"
                        >
                            {isLoading ? "Adding..." : "Add Tracker"}
                        </button>
                    </div>
                </div>
            </div>
        </div>

    );
}

export default FreelancerAddJobPostTrackerModal;
